import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import {
  OpenMode,
  PropertyOverrideType,
  TextAttachmentType,
  LeaderDirectionType,
} from "../core/constants";

export interface DRMLeaderStyleParams {
  textStyleName: string;
  textStyleId: DrawingModule.OdDbObjectId | null;
  textHeight: number;
  arrowSize: number;
  landingGap: number;
  doglegLength: number;
}

function buildDRStyleName(textStyleName: string, textHeight: number): string {
  const safe = textStyleName.replace(/[^a-zA-Z0-9_]/g, "_").slice(0, 40);
  const h = textHeight.toFixed(4).replace(/\.?0+$/, "") || "0";
  return `DR_${safe}_${h}`;
}

/**
 * Resolve an ODA text style OdDbObjectId to its name string.
 * Used to build a deterministic DR MLeaderStyle name per (textStyle, textHeight) pair.
 */
export function getTextStyleName(
  module: DrawingModuleType,
  textStyleId: DrawingModule.OdDbObjectId | null,
): string {
  if (!textStyleId || textStyleId.isNull()) return "default";
  try {
    const record = openAs(
      textStyleId,
      module.OdDbTextStyleTableRecord,
      OpenMode.kForRead,
    );
    const nameStr = record.getName();
    const name = nameStr.toString();
    deleteObj(nameStr);
    deleteObj(record);
    return name || "default";
  } catch {
    return "default";
  }
}

/**
 * Ensure a DR MLeaderStyle exists for the given (textStyleName, textHeight) pair
 * and return its ID.
 *
 * One style is created per unique combination so that:
 * - Leaders sharing the same style can be updated globally from AutoCAD.
 * - Leaders with different text styles or heights get their own style and
 *   therefore render correctly when entity-level overrides are cleared.
 *
 * All visual properties are stored on the style rather than as entity-level
 * overrides, which is exactly how native AutoCAD leaders behave.
 * The first call creates the style; subsequent calls with the same key reuse it.
 */
export function ensureDRMLeaderStyle(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  params: DRMLeaderStyleParams,
): DrawingModule.OdDbObjectId {
  const styleName = buildDRStyleName(params.textStyleName, params.textHeight);

  const dictId = db.getMLeaderStyleDictionaryId(true);
  const dict = openAs(dictId, module.OdDbDictionary, OpenMode.kForRead);
  deleteObj(dictId);

  const nameStr = new module.OdString(styleName);
  const alreadyExists = dict.has1(nameStr);

  if (alreadyExists) {
    const obj = dict.getAt1(nameStr, OpenMode.kForRead);
    const existingId = obj.objectId();
    deleteObj(obj);
    deleteObj(nameStr);
    deleteObj(dict);
    return existingId;
  }

  deleteObj(nameStr);
  deleteObj(dict);

  const style = module.OdDbMLeaderStyle.createObject();
  style.setDatabaseDefaults(db);

  style.setTextHeight(params.textHeight);
  style.setArrowSize(params.arrowSize);
  style.setLandingGap(params.landingGap);
  style.setDoglegLength(params.doglegLength);
  style.setEnableDogleg(true);
  style.setEnableLanding(true);
  style.setContentType(2); // kMTextContent
  style.setTextAttachmentType(
    TextAttachmentType.kAttachmentMiddle,
    LeaderDirectionType.kLeftLeader,
  );
  style.setTextAttachmentType(
    TextAttachmentType.kAttachmentMiddle,
    LeaderDirectionType.kRightLeader,
  );

  if (params.textStyleId && !params.textStyleId.isNull()) {
    style.setTextStyleId(params.textStyleId);
  }

  const postNameStr = new module.OdString(styleName);
  const styleId = style.postMLeaderStyleToDb(db, postNameStr);
  deleteObj(postNameStr);
  deleteObj(style);

  return styleId;
}

/**
 * Re-assign an existing MLeader (already opened for write) to the appropriate
 * DR style for the given (textStyleId, textHeight) pair.
 *
 * Reads geometric ratios (arrowSize, landingGap, doglegLength) from the
 * entity's current MLeaderStyle and scales them to the new textHeight, so
 * proportions are preserved across height changes.
 *
 * Clears all entity-level overrides so the entity fully inherits from the
 * new DR style, instead of pinning values to the entity.
 *
 * @returns the new DR style ID — caller must deleteObj() it when done.
 */
export function switchMLeaderStyle(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  mleader: DrawingModule.OdDbMLeader,
  textStyleId: DrawingModule.OdDbObjectId | null,
  textHeight: number,
): DrawingModule.OdDbObjectId {
  // Read geometric ratios from the entity's current effective style.
  const currentStyleId = mleader.MLeaderStyle();
  const currentStyle = openAs(
    currentStyleId,
    module.OdDbMLeaderStyle,
    OpenMode.kForRead,
  );
  const currentH = currentStyle.textHeight();
  const arrowSizeRatio =
    currentH > 0 ? currentStyle.arrowSize() / currentH : 0.6;
  const landingGapRatio =
    currentH > 0 ? currentStyle.landingGap() / currentH : 1.0;
  const doglegLengthRatio =
    currentH > 0 ? currentStyle.doglegLength() / currentH : 2.0;
  deleteObj(currentStyle);
  deleteObj(currentStyleId);

  const arrowSize = arrowSizeRatio * textHeight;
  const landingGap = landingGapRatio * textHeight;
  const doglegLength = doglegLengthRatio * textHeight;

  const drStyleId = ensureDRMLeaderStyle(module, db, {
    textStyleName: getTextStyleName(module, textStyleId),
    textStyleId,
    textHeight,
    arrowSize,
    landingGap,
    doglegLength,
  });

  mleader.setMLeaderStyle(drStyleId);

  if (textStyleId && !textStyleId.isNull()) {
    mleader.setTextStyleId(textStyleId);
  }
  mleader.setContentType(2); // kMTextContent
  mleader.setTextHeight(textHeight);
  mleader.setArrowSize(arrowSize);
  mleader.setEnableDogleg(true);
  mleader.setEnableLanding(true);
  mleader.setLandingGap(landingGap);
  mleader.setDoglegLength1(doglegLength);

  const OPT = PropertyOverrideType;
  mleader.setOverride(OPT.kTextStyleId, false);
  mleader.setOverride(OPT.kContentType, false);
  mleader.setOverride(OPT.kTextHeight, false);
  mleader.setOverride(OPT.kArrowSize, false);
  mleader.setOverride(OPT.kEnableDogleg, false);
  mleader.setOverride(OPT.kEnableLanding, false);
  mleader.setOverride(OPT.kLandingGap, false);
  mleader.setOverride(OPT.kDoglegLength, false);
  mleader.setOverride(OPT.kTextLeftAttachmentType, false);
  mleader.setOverride(OPT.kTextRightAttachmentType, false);

  return drStyleId;
}
